import { format, startOfDay } from 'date-fns';
import {
  ArrowLeftRight,
  Building2,
  Calendar,
  CheckCircle2,
  ClipboardList,
  ExternalLink,
  FileText,
  Flag,
  Pencil,
  User as UserIcon,
} from 'lucide-react';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { CustomerSearchCombobox } from '@/components/shared/customer-search-combobox';
import {
  CaseSummaryPanel,
  DisputeSummaryPanel,
  PromiseSummaryPanel,
} from '@/components/task-board/source-record-summary';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DatePicker } from '@/components/ui/date-picker';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import { UserCombobox } from '@/components/ui/user-combobox';
import type { CreatePromiseFormCustomer } from '@/hooks/useCreatePromiseData';
import { usePromiseById } from '@/hooks/usePromisesToPayData';
import { useCompanyUsersList } from '@/hooks/useUsersData';
import { buildTransactionDeepLink } from '@/lib/transaction-deep-link';
import { cn } from '@/lib/utils';
import type { UpdateTaskApiRequest } from '@/types/api/tasks';
import type { UnifiedTask } from '@/types/tasks';
import type { User } from '@/types/user';

// ---------------------------------------------------------------------------
// Validation constants (aligned with the backend / database schema)
// ---------------------------------------------------------------------------
const MAX_TITLE_LENGTH = 150;
const MAX_DESCRIPTION_LENGTH = 2000;

// ---------------------------------------------------------------------------
// Helpers extracted to reduce component cognitive complexity (S3776)
// ---------------------------------------------------------------------------

function getStatusBadgeVariant(status: string) {
  switch (status) {
    case 'completed':
      return 'secondary';
    case 'in_progress':
      return 'default';
    case 'pending':
      return 'outline';
    default:
      return 'outline';
  }
}

function PhoneCallSourceResolver({ sourceId, linkType }: { readonly sourceId: string; readonly linkType?: string }) {
  const shouldFetchPromise = !linkType || linkType === 'Promise';
  const { data: promise, isLoading } = usePromiseById(shouldFetchPromise ? sourceId : '');

  if (linkType === 'Dispute') {
    return <DisputeSummaryPanel disputeId={sourceId} />;
  }

  if (linkType === 'Case') {
    return <CaseSummaryPanel caseId={sourceId} />;
  }

  if (linkType === 'Promise') {
    return <PromiseSummaryPanel promiseId={sourceId} />;
  }

  if (isLoading) return null;

  return promise ? <PromiseSummaryPanel promiseId={sourceId} /> : <CaseSummaryPanel caseId={sourceId} />;
}

function SourceRecordSummary({
  sourceType,
  sourceId,
  linkType,
}: {
  readonly sourceType?: string;
  readonly sourceId?: string;
  readonly linkType?: string;
}) {
  if (!sourceType || !sourceId) return null;
  const sourceTypeLower = sourceType.toLowerCase();
  return (
    <>
      <Separator className="my-6" />
      {sourceTypeLower.includes('promise') && <PromiseSummaryPanel promiseId={sourceId} />}
      {sourceTypeLower.includes('phone') && <PhoneCallSourceResolver sourceId={sourceId} linkType={linkType} />}
      {!sourceTypeLower.includes('phone') &&
        (sourceTypeLower.includes('case') || sourceTypeLower.includes('collection')) && (
          <CaseSummaryPanel caseId={sourceId} />
        )}
      {sourceTypeLower.includes('dispute') && <DisputeSummaryPanel disputeId={sourceId} />}
    </>
  );
}

function TransactionReferencePanel({
  task,
  onNavigate,
}: {
  readonly task: UnifiedTask;
  readonly onNavigate: () => void;
}) {
  if (task.linkType !== 'Transaction' || !task.sourceId) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <ArrowLeftRight className="h-4 w-4" />
          Transaction
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Link to={buildTransactionDeepLink(task.transactionReference, task.sourceId)} onClick={onNavigate}>
          <Button variant="outline" size="sm" className="w-full">
            <ExternalLink className="h-3.5 w-3.5 mr-2" />
            View {task.transactionReference || task.sourceId}
          </Button>
        </Link>
      </CardContent>
    </Card>
  );
}

/** Helper to clear a specific validation error key from the errors map.
 *  Checks whether the key exists first to avoid unnecessary re-renders. */
function clearValidationError(
  key: string,
  currentErrors: Record<string, string>,
  setValidationErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>,
) {
  if (!currentErrors[key]) return;
  setValidationErrors((prev) => {
    const next = { ...prev };
    delete next[key];
    return next;
  });
}

// ---------------------------------------------------------------------------
// Sub-components extracted to reduce cognitive complexity (S3776).
// ---------------------------------------------------------------------------

interface DescriptionFieldProps {
  readonly isEditing: boolean;
  readonly isTaskType: boolean;
  readonly editedTask: UnifiedTask | null;
  readonly currentTask: UnifiedTask;
  readonly setEditedTask: React.Dispatch<React.SetStateAction<UnifiedTask | null>>;
  readonly validationErrors: Record<string, string>;
  readonly setValidationErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>;
}

function DescriptionField({
  isEditing,
  isTaskType,
  editedTask,
  currentTask,
  setEditedTask,
  validationErrors,
  setValidationErrors,
}: DescriptionFieldProps) {
  if (isEditing && isTaskType && editedTask) {
    return (
      <div className="space-y-1">
        <Textarea
          value={editedTask.description || ''}
          maxLength={MAX_DESCRIPTION_LENGTH}
          onChange={(e) => {
            setEditedTask({ ...editedTask, description: e.target.value });
            clearValidationError('description', validationErrors, setValidationErrors);
          }}
          rows={4}
          className="resize-none"
          placeholder="Task description..."
        />
        <div className="flex justify-between">
          {validationErrors.description && <p className="text-xs text-red-500">{validationErrors.description}</p>}
          <p
            className={cn(
              'text-xs text-muted-foreground ml-auto',
              (editedTask.description?.length ?? 0) > MAX_DESCRIPTION_LENGTH && 'text-red-500',
            )}
          >
            {editedTask.description?.length ?? 0}/{MAX_DESCRIPTION_LENGTH}
          </p>
        </div>
      </div>
    );
  }

  return <p className="text-sm text-muted-foreground">{currentTask.description || 'No description'}</p>;
}

// ---------------------------------------------------------------------------
// EditableFields — extracted as a standalone component so its internal
// conditionals do not count towards the parent's cognitive complexity.
// ---------------------------------------------------------------------------

interface EditableFieldsProps {
  readonly isTaskType: boolean;
  readonly isEditMode: boolean;
  readonly editedTask: UnifiedTask | null;
  readonly currentTask: UnifiedTask;
  readonly editedTitle: string;
  readonly setEditedTitle: React.Dispatch<React.SetStateAction<string>>;
  readonly setEditedTask: React.Dispatch<React.SetStateAction<UnifiedTask | null>>;
  readonly validationErrors: Record<string, string>;
  readonly setValidationErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  readonly companyUsers: User[];
  readonly selectedAssigneeId: string;
  readonly setSelectedAssigneeId: React.Dispatch<React.SetStateAction<string>>;
  readonly selectedCustomerForEdit: CreatePromiseFormCustomer | null;
  readonly setSelectedCustomerForEdit: React.Dispatch<React.SetStateAction<CreatePromiseFormCustomer | null>>;
}

function EditableFields({
  isTaskType,
  isEditMode,
  editedTask,
  currentTask,
  editedTitle,
  setEditedTitle,
  setEditedTask,
  validationErrors,
  setValidationErrors,
  companyUsers,
  selectedAssigneeId,
  setSelectedAssigneeId,
  selectedCustomerForEdit,
  setSelectedCustomerForEdit,
}: EditableFieldsProps) {
  const isEditing = isEditMode && editedTask !== null;

  return (
    <CardContent className="space-y-4">
      {/* Title - editable only for Task type */}
      {isTaskType && (
        <div className="space-y-1.5">
          <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
            <ClipboardList className="h-3 w-3" />
            Title
          </Label>
          {isEditing ? (
            <div className="space-y-1">
              <Input
                value={editedTitle}
                maxLength={MAX_TITLE_LENGTH}
                onChange={(e) => {
                  setEditedTitle(e.target.value);
                  clearValidationError('title', validationErrors, setValidationErrors);
                }}
                placeholder="Task title..."
                className="h-9"
              />
              <div className="flex justify-between">
                {validationErrors.title && <p className="text-xs text-red-500">{validationErrors.title}</p>}
                <p
                  className={cn(
                    'text-xs text-muted-foreground ml-auto',
                    editedTitle.length > MAX_TITLE_LENGTH && 'text-red-500',
                  )}
                >
                  {editedTitle.length}/{MAX_TITLE_LENGTH}
                </p>
              </div>
            </div>
          ) : (
            <p className="text-sm font-medium">{currentTask.title}</p>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        {/* Due Date - editable only for Task type */}
        <div className="space-y-1.5">
          <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
            <Calendar className="h-3 w-3" />
            Due Date
          </Label>
          {isEditing && isTaskType && editedTask ? (
            <DatePicker
              date={editedTask.dueDate ? new Date(editedTask.dueDate) : undefined}
              onDateChange={(date) => setEditedTask({ ...editedTask, dueDate: date || new Date() })}
            />
          ) : (
            <p className="text-sm font-medium">
              {currentTask.dueDate ? format(new Date(currentTask.dueDate), 'MMM dd, yyyy') : 'Not set'}
            </p>
          )}
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
            <UserIcon className="h-3 w-3" />
            Assigned To
          </Label>
          {isEditing && editedTask ? (
            <>
              <UserCombobox
                users={companyUsers}
                value={selectedAssigneeId}
                onValueChange={(userId) => {
                  setSelectedAssigneeId(userId);
                  const user = companyUsers.find((u) => u.id === userId);
                  if (editedTask && user) {
                    setEditedTask({
                      ...editedTask,
                      assignedTo: `${user.firstName} ${user.lastName}`,
                      assignedToId: user.id,
                    });
                  }
                  clearValidationError('assignedTo', validationErrors, setValidationErrors);
                }}
                placeholder="Assign to..."
              />
              {validationErrors.assignedTo && <p className="text-xs text-red-500">{validationErrors.assignedTo}</p>}
            </>
          ) : (
            <p className="text-sm">{currentTask.assignedTo || 'Unassigned'}</p>
          )}
        </div>

        {/* Customer - editable only for Task type */}
        {(currentTask.customerId || (isEditMode && isTaskType)) && (
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
              <Building2 className="h-3 w-3" />
              Customer
            </Label>
            {isEditing && isTaskType && editedTask ? (
              <CustomerSearchCombobox
                selectedCustomer={selectedCustomerForEdit}
                onSelectCustomer={(customer) => {
                  setSelectedCustomerForEdit(customer);
                  setEditedTask({
                    ...editedTask,
                    customerId: customer.id,
                    customerName: customer.name,
                  });
                }}
                placeholder="Select customer..."
              />
            ) : (
              <p className="text-sm">
                {currentTask.customerName || currentTask.customerAccountNumber || currentTask.customerId}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Description - editable only for Task type */}
      <div className="space-y-1.5 pt-2">
        <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
          <FileText className="h-3 w-3" />
          Description
        </Label>
        <DescriptionField
          isEditing={isEditing}
          isTaskType={isTaskType}
          editedTask={editedTask}
          currentTask={currentTask}
          setEditedTask={setEditedTask}
          validationErrors={validationErrors}
          setValidationErrors={setValidationErrors}
        />
      </div>
    </CardContent>
  );
}

interface TaskDetailsDrawerProps {
  task: UnifiedTask | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpdate?: (task: UnifiedTask) => void;
  /** Called when saving edits via the API */
  onSave?: (taskId: string, data: UpdateTaskApiRequest) => void;
  /** Called when completing a task via the API */
  onComplete?: (taskId: string) => void;
}

export const TaskDetailsDrawer: React.FC<TaskDetailsDrawerProps> = ({
  task,
  open,
  onOpenChange,
  onUpdate,
  onSave,
  onComplete,
}) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedTask, setEditedTask] = useState<UnifiedTask | null>(null);
  const [selectedAssigneeId, setSelectedAssigneeId] = useState('');
  const [editedTitle, setEditedTitle] = useState('');
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [selectedCustomerForEdit, setSelectedCustomerForEdit] = useState<CreatePromiseFormCustomer | null>(null);
  const { data: companyUsers = [] } = useCompanyUsersList();

  // Track the previous task id so we can detect when the user clicks a different task
  const prevTaskIdRef = useRef<string | null>(null);

  // Reset edit state whenever the task changes or the drawer closes
  useEffect(() => {
    const taskId = task?.id ?? null;
    if (taskId !== prevTaskIdRef.current || !open) {
      setIsEditMode(false);
      setEditedTask(null);
      setSelectedAssigneeId('');
      setSelectedCustomerForEdit(null);
      setValidationErrors({});
      setEditedTitle('');
    }
    prevTaskIdRef.current = taskId;
  }, [task?.id, open]);

  if (!task) return null;

  const currentTask = isEditMode && editedTask ? editedTask : task;

  const isTaskType = task.type === 'task';

  const handleEditClick = () => {
    setEditedTask({ ...task });
    setEditedTitle(task.title);
    setValidationErrors({});

    setSelectedAssigneeId(task.assignedToId || '');

    // Pre-select the saved customer so the combobox shows it instead of the placeholder
    if (task.customerId) {
      setSelectedCustomerForEdit({
        id: task.customerId,
        name: task.customerName || '',
        accountNumber: task.customerAccountNumber || '',
        email: '',
        phone: '',
      });
    } else {
      setSelectedCustomerForEdit(null);
    }

    setIsEditMode(true);
  };

  const validate = (): boolean => {
    const errors: Record<string, string> = {};

    if (isTaskType) {
      if (!editedTitle.trim()) {
        errors.title = 'Title is required.';
      } else if (editedTitle.trim().length > MAX_TITLE_LENGTH) {
        errors.title = `Title must not exceed ${MAX_TITLE_LENGTH} characters.`;
      }

      if (editedTask && editedTask.description.length > MAX_DESCRIPTION_LENGTH) {
        errors.description = `Description must not exceed ${MAX_DESCRIPTION_LENGTH} characters.`;
      }
    }

    if (!selectedAssigneeId) {
      errors.assignedTo = 'Assigned To is required.';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveClick = () => {
    if (!editedTask) return;
    if (!validate()) return;

    if (onSave && isTaskType) {
      const apiData: UpdateTaskApiRequest = {
        title: editedTitle.trim(),
        description: editedTask.description?.trim() || null,
        userAssignedId: selectedAssigneeId ? parseInt(selectedAssigneeId, 10) : 0,
        dueDate: format(new Date(editedTask.dueDate), 'yyyy-MM-dd'),
        customerId: editedTask.customerId ? parseInt(editedTask.customerId, 10) : null,
      };
      onSave(task.id, apiData);
      resetEditState();
      onOpenChange(false);
    } else if (onUpdate) {
      // Demo mode or non-task type fallback
      const updatedTask = {
        ...editedTask,
        title: isTaskType ? editedTitle.trim() : editedTask.title,
      };
      onUpdate(updatedTask);
      resetEditState();
    }
  };

  const resetEditState = () => {
    setIsEditMode(false);
    setEditedTask(null);
    setSelectedAssigneeId('');
    setSelectedCustomerForEdit(null);
    setValidationErrors({});
  };

  const handleCancelEdit = () => {
    resetEditState();
  };

  const handleMarkComplete = () => {
    if (onComplete) {
      onComplete(task.id);
      onOpenChange(false);
    } else if (onUpdate) {
      onUpdate({ ...task, status: 'completed' });
    }
  };

  // Overdue: strictly before today (not including today itself)
  const todayStart = startOfDay(new Date());
  const dueDateStart = task.dueDate ? startOfDay(new Date(task.dueDate)) : null;
  const isOverdue = dueDateStart !== null && dueDateStart < todayStart && task.status !== 'completed';

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-[700px] p-0 flex flex-col">
        {/* Header */}
        <SheetHeader className="px-6 py-4 border-b space-y-1">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <SheetTitle className="text-xl font-semibold">{task.title}</SheetTitle>
              <div className="flex items-center gap-2 mt-2">
                <Badge variant="outline" className="text-xs">
                  {task.id}
                </Badge>
                <Badge variant={getStatusBadgeVariant(task.status)}>{task.status.replace('_', ' ')}</Badge>
              </div>
            </div>
          </div>
        </SheetHeader>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
          {/* Overdue Alert */}
          {isOverdue && (
            <Card className="border-destructive/50 bg-destructive/5">
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <div className="rounded-full bg-destructive/10 p-2">
                    <Flag className="h-4 w-4 text-destructive" />
                  </div>
                  <div>
                    <p className="font-semibold text-sm text-destructive">Task Overdue</p>
                    <p className="text-xs text-muted-foreground">
                      This task was due on {format(new Date(task.dueDate), 'PPP')}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Task Details Card */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <ClipboardList className="h-4 w-4" />
                  Task Details
                </CardTitle>
                {!isEditMode ? (
                  isTaskType &&
                  task.status !== 'completed' && (
                    <Button size="sm" variant="outline" onClick={handleEditClick}>
                      <Pencil className="h-3 w-3 mr-1" />
                      Edit
                    </Button>
                  )
                ) : (
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={handleCancelEdit}>
                      Cancel
                    </Button>
                    <Button size="sm" onClick={handleSaveClick}>
                      Save Changes
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <EditableFields
              isTaskType={isTaskType}
              isEditMode={isEditMode}
              editedTask={editedTask}
              currentTask={currentTask}
              editedTitle={editedTitle}
              setEditedTitle={setEditedTitle}
              setEditedTask={setEditedTask}
              validationErrors={validationErrors}
              setValidationErrors={setValidationErrors}
              companyUsers={companyUsers}
              selectedAssigneeId={selectedAssigneeId}
              setSelectedAssigneeId={setSelectedAssigneeId}
              selectedCustomerForEdit={selectedCustomerForEdit}
              setSelectedCustomerForEdit={setSelectedCustomerForEdit}
            />
          </Card>

          {/* Mark as Complete Button - For Task and Phone Call types */}
          {(task.sourceType === 'Task' || task.sourceType === 'Phone Call') && task.status !== 'completed' && (
            <Button onClick={handleMarkComplete} className="w-full" size="lg">
              <CheckCircle2 className="h-4 w-4 mr-2" />
              Mark as Complete
            </Button>
          )}

          <TransactionReferencePanel task={task} onNavigate={() => onOpenChange(false)} />

          <SourceRecordSummary sourceType={task.sourceType} sourceId={task.sourceId} linkType={task.linkType} />
        </div>
      </SheetContent>
    </Sheet>
  );
};
